import { useEffect, useState } from "react";
import { NumberFormatter, Table } from "@mantine/core";
import moment from "moment";
import QRCode from "react-qr-code";
import {
  Mail,
  Phone,
  ArrowRight,
} from "lucide-react";

import { humanizeText, numberToWords } from "utils/utility";
import { useUsersData } from "libs/data-access/src/api/usersData/useUsersData";
import { IFlightOnlineBookingList, IFlightOnlineBookingPaymentOrder, IUserDetailsForInvoice } from "libs/data-access/types";


interface IFareBreakup {
  airlineBaseFare: number;
  airlineTax: number;
  airlineSsrCharge: number;
  airlineSeatCharge: number;
  serviceCharges: number;
  otherCharges: number;
  totalGst: number;
  commission: number;
  commissionTds: number;
  totalFare: number;
};

interface IProps {
  flightTicket: IFlightOnlineBookingList;
};

export default function FlightPaymentAdvice({ flightTicket }: IProps) {

  const { getUsersData } = useUsersData();

  const [userDetails, setUserDetails] = useState<IUserDetailsForInvoice>({
    id: 0,
    name: "",
    emailId: "",
    phoneNumber: "",
    affiliateDetailForInvoice: {
      country: null,
      city: null,
      state: null,
      pinCode: null,
      firstAddress: null,
      secondAddress: null,
      companyName: null,
    }
  });

  const [fareDetails, setFareDetails] = useState<{
    paymentDetails: IFlightOnlineBookingPaymentOrder | undefined;
    supplierId: number;
    fareBreakup: IFareBreakup;
  }>({
    paymentDetails: undefined,
    supplierId: 0,
    fareBreakup: {
      airlineBaseFare: 0,
      airlineTax: 0,
      airlineSsrCharge: 0,
      airlineSeatCharge: 0,
      serviceCharges: 0,
      otherCharges: 0,
      totalGst: 0,
      commission: 0,
      commissionTds: 0,
      totalFare: 0,
    }
  });


  useEffect(() => {
    getUserDetails();
    manageFareBreakup();
  }, [flightTicket]);


  // HANDLER FUNCTIONS
  const manageFareBreakup = () => {

    const paymentDetails = flightTicket?.paymentOrder;
    const supplierId = flightTicket?.bookingResponses?.[0]?.flightSupplierId;

    let fareBreakup: IFareBreakup = {
      airlineBaseFare: 0,
      airlineTax: 0,
      airlineSsrCharge: 0,
      airlineSeatCharge: 0,
      serviceCharges: 0,
      otherCharges: 0,
      totalGst: 0,
      commission: 0,
      commissionTds: 0,
      totalFare: 0,
    };

    // airline base fare
    fareBreakup.airlineBaseFare =
      (paymentDetails?.galelioFlightOrderDetail?.flightBasePrice || 0) +
      (paymentDetails?.naviatorOrderDetail?.indigoBasePrice || 0) +
      (paymentDetails?.akbarOrderDetail?.akbarBasePrice || 0);

    // airline tax
    fareBreakup.airlineTax =
      (paymentDetails?.galelioFlightOrderDetail?.flightTaxPrice || 0) +
      (paymentDetails?.naviatorOrderDetail?.indigoTaxPrice || 0) +
      (paymentDetails?.akbarOrderDetail?.akbarTaxPrice || 0);

    // airline ssr charge
    fareBreakup.airlineSsrCharge =
      (paymentDetails?.naviatorOrderDetail?.indigoSsrPrice || 0) +
      (paymentDetails?.akbarOrderDetail?.akbarSsrPrice || 0);

    // airline seat charge
    fareBreakup.airlineSeatCharge =
      (paymentDetails?.naviatorOrderDetail?.indigoSeatPrice || 0) +
      (paymentDetails?.akbarOrderDetail?.akbarSeatPrice || 0);

    // service charges
    fareBreakup.serviceCharges =
      (paymentDetails?.adminBaseCharge || 0) +
      (paymentDetails?.adminServiceCharge || 0) +
      (paymentDetails?.adminBookingCharge || 0) +
      (paymentDetails?.affiliateBaseCharge || 0) +
      (paymentDetails?.affiliateServiceCharge || 0) +
      (paymentDetails?.affiliateBookingCharge || 0);

    // other charges
    fareBreakup.otherCharges = (paymentDetails?.adminOtherCharge || 0);

    // total gst
    fareBreakup.totalGst = (paymentDetails?.adminTotalGst || 0);

    // commission
    fareBreakup.commission = Number(
      (
        (paymentDetails?.commissionBaseFare || 0) +
        (paymentDetails?.commissionPlbBaseFare || 0) +
        (paymentDetails?.commissionPlbBaseFareYQ || 0) +
        (paymentDetails?.commissionPlbBaseFareYR || 0) +
        (paymentDetails?.commissionPlbYQ || 0) +
        (paymentDetails?.commissionPlbYR || 0) +
        (paymentDetails?.commissionSurcharge || 0)
      ).toFixed(2)
    );

    // commission tds
    fareBreakup.commissionTds = (paymentDetails?.commissionTds || 0);

    // total fare
    fareBreakup.totalFare =
      (fareBreakup.airlineBaseFare +
        fareBreakup.airlineTax +
        fareBreakup.airlineSsrCharge +
        fareBreakup.airlineSeatCharge +
        fareBreakup.serviceCharges +
        fareBreakup.otherCharges +
        fareBreakup.totalGst +
        fareBreakup.commission +
        fareBreakup.commissionTds) -
      fareBreakup.commission;
    // (paymentDetails?.galelioFlightOrderDetail?.flightBasePrice || 0) +
    // (paymentDetails?.galelioFlightOrderDetail?.flightTaxPrice || 0) +
    // (paymentDetails?.naviatorOrderDetail?.indigoBasePrice || 0) +
    // (paymentDetails?.naviatorOrderDetail?.indigoTaxPrice || 0) +
    // (paymentDetails?.akbarOrderDetail?.akbarBasePrice || 0) +
    // (paymentDetails?.akbarOrderDetail?.akbarTaxPrice || 0) +
    // (paymentDetails?.adminBaseCharge || 0) +
    // (paymentDetails?.adminServiceCharge || 0) +
    // (paymentDetails?.adminBookingCharge || 0) +
    // (paymentDetails?.adminOtherCharge || 0) +
    // (paymentDetails?.adminTotalGst || 0) +
    // (paymentDetails?.affiliateBaseCharge || 0) +
    // (paymentDetails?.affiliateServiceCharge || 0) +
    // (paymentDetails?.affiliateBookingCharge || 0) +
    // (paymentDetails?.commissionTds || 0) - fareBreakup.commission;
    // (paymentDetails?.affiliateOtherCharges || 0) +
    // (paymentDetails?.affiliateTotalGst || 0);

    setFareDetails({
      paymentDetails,
      supplierId,
      fareBreakup
    });

  };


  // API CALLS
  const getUserDetails = async () => {

    if (!flightTicket?.bookingResponses?.[0]?.id) return;

    const response = await getUsersData(`/details/invoice?bookingId=${flightTicket?.bookingResponses?.[0]?.id}&category=${"FLIGHT"}`);

    if (response.isSuccess) {
      // console.log("Response for user details: ", response.data);
      setUserDetails(response.data?.data);
    } else {
      console.log("Error while fetching user details: ", response.data);
    };
  };


  return (
    <div className="p-6 max-w-5xl mx-auto bg-white rounded-lg print:shadow-none">
      {/* HEADER */}
      <div className="flex justify-between items-start pb-6 border-b border-gray-300">
        <div className="text-gray-800">
          <img
            className="h-20 w-auto mb-3"
            src={require("../../../../../assets/logos/travelyatra.jpeg")}
            alt="Paul Merchants Logo"
          />
          <p className="text-base font-semibold">Paul Merchants Ltd.</p>
          <div className="text-xs flex items-center gap-1">
            SCO 829-830, Sector 22A Chandigarh, Chandigarh
          </div>
          <div className="text-xs">FE.CG/AD-II/01/2008 NDL-ADII-0001-2023</div>
          <div className="text-xs mt-2">
            <span className="font-semibold">GSTIN No: </span>
            <span>04AAACP5609D1ZW</span>,
            <span className="font-semibold ml-1"> CIN No: </span>
            <span>L74900DL1984PLC018679</span>
          </div>
          <div className="text-xs flex gap-4 items-center">
            <div className="flex items-center gap-1">
              <Phone className="size-3.5" />
              <span>0172 - 5041735, 01725041735</span>
            </div>
            <div className="flex items-center gap-1">
              <Mail className="size-3.5" />
              <span>del.travelaccounts@paulmerchants.net</span>
            </div>
          </div>
        </div>
        <div className="text-right flex flex-col justify-end items-end text-gray-700 text-sm">
          <div className="text-4xl font-bold tracking-tight text-gray-800 mb-2">
            Payment Advice
          </div>
          {flightTicket?.bookingResponses?.[0]?.invoiceNo && (
            <div>
              No. {flightTicket?.invoiceNo || ""}
            </div>
          )}
          <div>
            Issue on{" "}
            {flightTicket?.bookingResponses?.[0]?.bookingDate
              ?
              moment.parseZone(flightTicket?.bookingResponses?.[0]?.bookingDate).format("DD/MM/YYYY")
              :
              ""}
          </div>
        </div>
      </div>

      {/* CUSTOMER DETAILS */}
      <div className="my-3 flex justify-between items-center html2pdf__page-break--avoid">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-600 mb-1 font-medium">To,</p>
            <p className="text-lg font-semibold text-gray-800">
              {userDetails.name}
            </p>
            {
              userDetails.affiliateDetailForInvoice.companyName
                ?
                <p className="font-semibold text-gray-800">
                  {userDetails.affiliateDetailForInvoice.companyName}
                </p>
                :
                <></>
            }
            <p className="text-xs text-gray-600 mt-1">
              {userDetails.emailId}
            </p>
            <p className="text-xs text-gray-600 mt-1">
              {userDetails.phoneNumber}
            </p>
            {
              userDetails.affiliateDetailForInvoice.firstAddress
                ?
                <>
                  <p className="text-xs text-gray-600 mt-1">
                    {`${userDetails.affiliateDetailForInvoice.firstAddress}, ${userDetails.affiliateDetailForInvoice.secondAddress}, ${userDetails.affiliateDetailForInvoice.city}, ${userDetails.affiliateDetailForInvoice.state}, ${userDetails.affiliateDetailForInvoice.pinCode}`}
                  </p>
                  <p className="text-xs font-medium text-gray-700">
                    Place of Supply:{" "}
                    <span className="text-gray-600 font-normal">
                      {`${userDetails.affiliateDetailForInvoice.state}, ${userDetails.affiliateDetailForInvoice.country}`}
                    </span>
                  </p>
                </>
                :
                <></>
            }
          </div>
        </div>

        <div className="w-24 h-24">
          <QRCode
            size={50}
            value={`Payment Advice: ${flightTicket?.bookingResponses?.[0]?.invoiceNo || "N/A"
              }`}
            className="h-full w-full"
          />
        </div>
      </div>

      {/* JOURNEY DETAILS */}
      <div className="mb-6">
        {/* <div className="flex items-center gap-2 mb-3">
          <div className="h-5 w-1 bg-blue-500 rounded-full"></div>
          <p className="text-base font-semibold text-gray-800">
            Transaction Details: Air Ticket
          </p>
        </div> */}

        <div className="overflow-x-auto rounded-md border border-gray-300 html2pdf__page-break--avoid">
          <Table className="min-w-full">
            <thead>
              <tr className="bg-gray-100 text-left">
                <th className="text-sm font-medium text-gray-900 p-2 border-b border-gray-300">
                  Ticket/PNR
                </th>
                {/* <th className="text-sm font-medium text-gray-900 p-2 border-b border-gray-300">
                  Passenger
                </th> */}
                <th className="text-sm font-medium text-gray-900 p-2 border-b border-gray-300">
                  Route
                </th>
                <th className="text-sm font-medium text-gray-900 p-2 border-b border-gray-300">
                  Flight Details
                </th>
                <th className="text-sm font-medium text-gray-900 p-2 border-b border-gray-300">
                  Travel Date
                </th>
                {/* <th className="text-sm font-medium text-gray-900 p-2 border-b border-gray-300 text-right">
                  Total
                </th> */}
              </tr>
            </thead>
            <tbody>
              {/* GALILEO */}
              {
                flightTicket?.bookingResponses?.map((journey) =>
                  journey.galileoBookingDetailResponses?.map((each, index) => (
                    <tr key={each + "__1g_booking" + index}>
                      <td className="p-2 text-xs text-gray-900 border-b border-gray-300">
                        <div className="font-medium">Ticket: {journey.pnrDetail?.ticketNumber || "N/A"}</div>
                        <div className="text-gray-600 mt-1 font-medium">PNR: {journey.pnrDetail?.supplierPnr || "N/A"}</div>
                      </td>
                      {/* <td className="p-2 text-xs text-gray-900 border-b border-gray-300">
                        {journey.galileoPassengerDetailResponses?.map(each => `${each.title ?? ""} ${each.firstName ?? ""} ${each.lastName ?? ""}`).join(", ")}
                      </td> */}
                      <td className="p-2 text-xs text-gray-900 border-b border-gray-300 font-medium">
                        <div className="flex items-center gap-0.5">
                          {each.origin} <ArrowRight className="size-3.5 text-gray-500" /> {each.destination}
                          {/* MUM <ArrowRight className="size-3.5 text-gray-500" /> CAI
                              <ArrowRight className="size-3.5 text-gray-500" />
                              BER */}
                        </div>
                      </td>
                      <td className="p-2 text-xs text-gray-900 border-b border-gray-300">
                        <div className="">
                          <div className="font-medium">{each.flightBookingSegments?.map(each => `${each.carrier} ${each.flightNumber}`).join(", ")}</div>
                          <div>
                            {/* <span className="">MS 969</span> */}
                            <span className="text-gray-600 ml-1">({each.flightBookingSegments?.map(each => humanizeText(each.cabin)).join(", ")})</span>
                          </div>
                        </div>
                      </td>
                      <td className="p-2 text-xs text-gray-900 border-b border-gray-300">
                        {each.flightBookingSegments?.map(each => moment.parseZone(each.departureTime).format("DD/MM/YYYY HH:mm A"))?.[0]}
                      </td>
                      {/* <td className="p-2 text-xs font-medium text-gray-900 border-b border-gray-300 text-right">
                        ₹11,910.00
                      </td> */}
                    </tr>
                  )))
              }
              {/* NAVITAIRE */}
              {
                flightTicket?.bookingResponses?.map((journey) =>
                  journey.navitaireBookingDetailResponses?.map((each, index) => (
                    <tr key={each + "__nav_booking" + index}>
                      <td className="p-2 text-xs text-gray-900 border-b border-gray-300">
                        <div className="font-medium">Ticket: {journey.pnrDetail?.ticketNumber || "N/A"}</div>
                        <div className="text-gray-600 mt-1 font-medium">PNR: {journey.pnrDetail?.supplierPnr || "N/A"}</div>
                      </td>
                      {/* <td className="p-2 text-xs text-gray-900 border-b border-gray-300">
                        {journey.galileoPassengerDetailResponses?.map(each => `${each.title ?? ""} ${each.firstName ?? ""} ${each.lastName ?? ""}`).join(", ")}
                      </td> */}
                      <td className="p-2 text-xs text-gray-900 border-b border-gray-300 font-medium">
                        <div className="flex items-center gap-0.5">
                          {each.origin} <ArrowRight className="size-3.5 text-gray-500" /> {each.destination}
                          {/* MUM <ArrowRight className="size-3.5 text-gray-500" /> CAI
                              <ArrowRight className="size-3.5 text-gray-500" />
                              BER */}
                        </div>
                      </td>
                      <td className="p-2 text-xs text-gray-900 border-b border-gray-300">
                        <div className="">
                          <div className="font-medium">{each.navitaireBookingDetailSegments?.map(each => `${each.carrierCode} ${each.flightNumber}`).join(", ")}</div>
                          {/* <div>
                            <span className="">MS 969</span>
                            <span className="text-gray-600 ml-1">({each.navitaireBookingDetailSegments?.map(each => humanizeText(each.cabin)).join(", ")})</span>
                          </div> */}
                        </div>
                      </td>
                      <td className="p-2 text-xs text-gray-900 border-b border-gray-300">
                        {each.navitaireBookingDetailSegments?.map(each => moment.parseZone(each.originDateTime).format("DD/MM/YYYY HH:mm A"))?.[0]}
                      </td>
                      {/* <td className="p-2 text-xs font-medium text-gray-900 border-b border-gray-300 text-right">
                        ₹11,910.00
                      </td> */}
                    </tr>
                  )))
              }
              {/* AKBAR */}
              {
                flightTicket?.bookingResponses?.map((journey) =>
                  journey.akbarBookingDetailResponses?.map((each, index) => (
                    <tr key={each + "__ak_booking" + index}>
                      <td className="p-2 text-xs text-gray-900 border-b border-gray-300">
                        <div className="font-medium">Ticket: {journey.pnrDetail?.ticketNumber || "N/A"}</div>
                        <div className="text-gray-600 mt-1 font-medium">PNR: {journey.pnrDetail?.supplierPnr || "N/A"}</div>
                      </td>
                      {/* <td className="p-2 text-xs text-gray-900 border-b border-gray-300">
                      {journey.galileoPassengerDetailResponses?.map(each => `${each.title ?? ""} ${each.firstName ?? ""} ${each.lastName ?? ""}`).join(", ")}
                    </td> */}
                      <td className="p-2 text-xs text-gray-900 border-b border-gray-300 font-medium">
                        <div className="flex items-center gap-0.5">
                          {each.origin} <ArrowRight className="size-3.5 text-gray-500" /> {each.destination}
                        </div>
                      </td>
                      <td className="p-2 text-xs text-gray-900 border-b border-gray-300">
                        <div className="">
                          <div className="font-medium">{each.akbarBookingDetailJourneys?.map(each => `${each.oac} ${each.flightNo}`).join(", ")}</div>
                          {/* <div>
                            <span className="">MS 969</span>
                            <span className="text-gray-600 ml-1">({each.akbarBookingDetailJourneys?.map(each => humanizeText(each.cabin)).join(", ")})</span>
                          </div> */}
                        </div>
                      </td>
                      <td className="p-2 text-xs text-gray-900 border-b border-gray-300">
                        {each.akbarBookingDetailJourneys?.map(each => moment.parseZone(each.departureTime).format("DD/MM/YYYY HH:mm A"))?.[0]}
                      </td>
                      {/* <td className="p-2 text-xs font-medium text-gray-900 border-b border-gray-300 text-right">
                      ₹11,910.00
                    </td> */}
                    </tr>
                  )))
              }
            </tbody>
          </Table>
        </div>

        {/* <p className="text-sm mt-2 text-gray-600 italic">IAT(Date Change)</p> */}
      </div>

      {/* FARE SUMMARY */}
      <div className="flex justify-between items-end gap-4 mb-8 html2pdf__page-break--avoid">
        <div className="w-6/12 h-fit">
          <div className="flex items-center gap-2">
            <div className="text-sm font-medium text-gray-800">
              Amount in words:
              <div className="font-normal text-gray-600">
                {numberToWords(fareDetails.fareBreakup.totalFare)}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end w-5/12">
          <div className="w-full border border-gray-300 rounded-md p-3">
            <h3 className="text-sm font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-300">
              Payment Summary
            </h3>

            <div className="mb-4">
              {/* <h4 className="text-sm font-medium text-gray-800 mb-2">
                Fare Breakdown:
              </h4> */}
              <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-300 space-y-1.5">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Basic Fare</span>
                  <span className="text-gray-800">
                    <NumberFormatter
                      thousandSeparator
                      prefix={"₹"}
                      value={fareDetails.fareBreakup.airlineBaseFare}
                    />
                  </span>
                </div>

                {fareDetails.fareBreakup.airlineTax
                  ?
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Airline Tax & Other</span>
                    <span className="text-gray-800">
                      <NumberFormatter
                        thousandSeparator
                        prefix={"₹"}
                        value={fareDetails.fareBreakup.airlineTax}
                      />
                    </span>
                  </div>
                  :
                  <></>}

                {fareDetails.fareBreakup.airlineSsrCharge
                  ?
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Special Service Charge</span>
                    <span className="text-gray-800">
                      <NumberFormatter
                        thousandSeparator
                        prefix={"₹"}
                        value={fareDetails.fareBreakup.airlineSsrCharge}
                      />
                    </span>
                  </div>
                  :
                  <></>}

                <div className="flex justify-between text-sm font-medium pt-1.5 mt-1 border-t border-gray-300">
                  <span>Ticket Amount</span>
                  <span>
                    <NumberFormatter
                      thousandSeparator
                      prefix={"₹"}
                      value={
                        fareDetails.fareBreakup.airlineBaseFare +
                        fareDetails.fareBreakup.airlineTax +
                        fareDetails.fareBreakup.airlineSsrCharge
                      }
                    />
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              {fareDetails.fareBreakup.serviceCharges
                ?
                <div className="flex justify-between">
                  <p className="text-sm text-gray-600">Service Charges</p>
                  <p className="text-sm font-medium text-gray-800">
                    <NumberFormatter
                      thousandSeparator
                      prefix={"₹"}
                      value={fareDetails.fareBreakup.serviceCharges}
                    />
                  </p>
                </div>
                :
                <></>}
              {fareDetails.fareBreakup.otherCharges
                ?
                <div className="flex justify-between">
                  <p className="text-sm text-gray-600">Other Charge</p>
                  <p className="text-sm font-medium text-gray-800">
                    <NumberFormatter
                      thousandSeparator
                      prefix={"₹"}
                      value={fareDetails.fareBreakup.otherCharges}
                    />
                  </p>
                </div>
                :
                <></>}
              {fareDetails.fareBreakup.totalGst
                ?
                <div className="flex justify-between">
                  <p className="text-sm text-gray-600">GST On Charges</p>
                  <p className="text-sm font-medium text-gray-800">
                    <NumberFormatter
                      thousandSeparator
                      prefix={"₹"}
                      value={fareDetails.fareBreakup.totalGst}
                    />
                  </p>
                </div>
                :
                <></>}

              {fareDetails.fareBreakup.commission
                ?
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-800 mb-2">
                    Commission Breakdown:
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-300 space-y-1.5">
                    {fareDetails.paymentDetails?.commissionBaseFare
                      ?
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Base</span>
                        <span className="text-gray-800">
                          <NumberFormatter
                            thousandSeparator
                            prefix={"₹"}
                            value={fareDetails.paymentDetails?.commissionBaseFare || 0}
                          />
                        </span>
                      </div>
                      :
                      <></>}
                    {fareDetails.paymentDetails?.commissionSurcharge
                      ?
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">YQ</span>
                        <span className="text-gray-800">
                          <NumberFormatter
                            thousandSeparator
                            prefix={"₹"}
                            value={fareDetails.paymentDetails?.commissionSurcharge || 0}
                          />
                        </span>
                      </div>
                      :
                      <></>}
                    {fareDetails.paymentDetails?.commissionPlbBaseFare
                      ?
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Base (PLB)</span>
                        <span className="text-gray-800">
                          <NumberFormatter
                            thousandSeparator
                            prefix={"₹"}
                            value={fareDetails.paymentDetails?.commissionPlbBaseFare || 0}
                          />
                        </span>
                      </div>
                      :
                      <></>}
                    {fareDetails.paymentDetails?.commissionPlbYQ
                      ?
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">YQ (PLB)</span>
                        <span className="text-gray-800">
                          <NumberFormatter
                            thousandSeparator
                            prefix={"₹"}
                            value={fareDetails.paymentDetails?.commissionPlbYQ || 0}
                          />
                        </span>
                      </div>
                      :
                      <></>}
                    {fareDetails.paymentDetails?.commissionPlbYR
                      ?
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">YR (PLB)</span>
                        <span className="text-gray-800">
                          <NumberFormatter
                            thousandSeparator
                            prefix={"₹"}
                            value={fareDetails.paymentDetails?.commissionPlbYR || 0}
                          />
                        </span>
                      </div>
                      :
                      <></>}
                    {fareDetails.paymentDetails?.commissionPlbBaseFareYQ
                      ?
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">BASE + YQ (PLB)</span>
                        <span className="text-gray-800">
                          <NumberFormatter
                            thousandSeparator
                            prefix={"₹"}
                            value={fareDetails.paymentDetails?.commissionPlbBaseFareYQ || 0}
                          />
                        </span>
                      </div>
                      :
                      <></>}
                    {fareDetails.paymentDetails?.commissionPlbBaseFareYR
                      ?
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">BASE + YR (PLB)</span>
                        <span className="text-gray-800">
                          <NumberFormatter
                            thousandSeparator
                            prefix={"₹"}
                            value={fareDetails.paymentDetails?.commissionPlbBaseFareYR || 0}
                          />
                        </span>
                      </div>
                      :
                      <></>}

                    <div className="flex justify-between text-sm font-medium pt-1.5 mt-1 border-t border-gray-300">
                      <span>Total Commission</span>
                      <span>
                        <NumberFormatter
                          thousandSeparator
                          prefix={"₹"}
                          value={fareDetails.fareBreakup.commission}
                        />
                      </span>
                    </div>
                  </div>
                </div>
                :
                <></>}

              {/* <div className="flex justify-between">
                <p className="text-sm text-gray-600">Commission (-)</p>
                <p className="text-sm font-medium text-gray-800">
                  <NumberFormatter
                    thousandSeparator
                    prefix={"₹"}
                    value={fareDetails.fareBreakup.commission}
                  />
                </p>
              </div> */}
              {fareDetails.fareBreakup.commissionTds
                ?
                <div className="flex justify-between">
                  <p className="text-sm text-gray-600">TDS On Commission (+)</p>
                  <p className="text-sm font-medium text-gray-800">
                    <NumberFormatter
                      thousandSeparator
                      prefix={"₹"}
                      value={fareDetails.fareBreakup.commissionTds}
                    />
                  </p>
                </div>
                :
                <></>}
              <div className="flex justify-between font-bold text-gray-800 pt-2 mt-2 border-t border-gray-300">
                <p>Total Amount</p>
                <NumberFormatter
                  thousandSeparator
                  prefix={"₹"}
                  value={Number(fareDetails.fareBreakup.totalFare.toFixed(2))}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Instructions - Simple Version */}
      <div className="mb-6 border-t border-gray-300 pt-4 text-gray-800 html2pdf__page-break--avoid">
        <p className="mb-1 text-xs"><span className="font-medium">Cash:</span> Payment should be made directly to chief cashier in our office and to no one else.</p>
        <p className="mb-1 text-xs"><span className="font-medium">Receipts:</span> Receipt on our official receipt form duly signed by our cashier only will be considered valid.</p>
        <p className="mb-1 text-xs"><span className="font-medium">Cheque:</span> All cheques should be drawn in favour of Paul Merchants Ltd and crossed a/c payee.</p>
        <p className="mb-1 text-xs"><span className="font-medium">Outstandings:</span> We reserve the right to charge interest @ 24% per annum on all outstanding overdue account.</p>
        <p className="mt-2 mb-1 text-xs"><span className="font-medium">All payments to be transferred directly into below account</span></p>
        <p className="mb-1 text-xs"><span className="font-medium">HDFC account number 00350330000694 IFSC HDFC0000035</span></p>
        <p className="text-xs">HSN/SAC-996551</p>
      </div>

      {/* FOOTER */}
      <div className="border-t border-gray-300 pt-4 mt-6 html2pdf__page-break--avoid">
        <div className="flex justify-between items-center">
          {/* <div className="w-16 h-16">
            <QRCode
              size={50}
              value={`Payment Advice: ${flightTicket?.bookingResponses?.[0]?.invoiceNo || "N/A"
                }`}
              className="h-full w-full"
            />
          </div> */}
          <p className="text-center text-gray-800 text-xs font-medium">
            This is a computer generated Payment Advice and does not require
            Signature/Stamp
          </p>
          <div className="text-right text-gray-800">
            <p className="text-xs">Generated on: {new Date().toLocaleDateString()}</p>
            <p className="mt-1 text-xs">Paul Merchants Ltd.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
